import { Collection, CollectionItem, FullItems, Video } from "@/api";
import { ContentThumbnail } from "@/components";
import { useFullItems } from "@/hooks";
import { useNavigateToItem } from "@/hooks/useNavigateToItem";
import { useCourseCompletion } from "../hooks/useCourseCompletion";
import { Completed } from "@/icons";
import { cn } from "@/utils";

interface ItemListProps {
  items: CollectionItem[];
  fullItems: FullItems;
  parentId: string | number;
  courseId: number;
}

const getItemTitle = (item: Collection | Video) => {
  if ("Title" in item) {
    return item.Title;
  }
  return item.Name;
};

export const ItemList = ({
  items,
  fullItems,
  parentId,
  courseId,
}: ItemListProps) => {
  const { getFullItem } = useFullItems(fullItems);
  const { navigateToItem } = useNavigateToItem();
  const { isVideoWatched } = useCourseCompletion(courseId);

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, idx) => {
        const fullItem = getFullItem(item);
        if (!fullItem) {
          return null;
        }
        // The hover overhangs full width of the parent 12px on each side
        // So we add 24px to full width and have 12px negative margins on each side
        // If isSidePanel, do not apply negative margins or width overhang
        return (
          <div
            key={`${item.ChildId}-${idx}`}
            className={cn(
              "flex items-center gap-5 p-3 cursor-pointer rounded-lg hover:bg-surface-secondary",
              "-mx-3 w-[calc(100%+24px)]"
            )}
            onClick={() =>
              navigateToItem({ item, fullItem, parentId, courseId })
            }
            tabIndex={0}
            role="button"
          >
            <ContentThumbnail
              thumbnail={item.Thumbnail}
              contentItem={fullItem}
              className="w-[160px] h-[90px] rounded-[8px] flex-shrink-0"
            />
            <h3 className="p1-semibold flex-1">{getItemTitle(fullItem)}</h3>
            {isVideoWatched(item.ChildId) && (
              <Completed className="h-6 w-6 text-success-item ml-2 flex-shrink-0" />
            )}
          </div>
        );
      })}
    </div>
  );
};
